import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';
import * as moment from 'moment';

import {
    ActualDataType,
    AggregationConfiguration,
    AggregationMode,
    GetAggregatedValuesDto,
    IBindable,
    IValueViewModel,
    TableWidgetDto,
    TagDateValue,
    TimeInterval,
} from '../../models';
import {
    IAggregatedValuesService,
    ICurrentApplicationService,
    IDateAlignmentHelperService,
    IFormatNumberHelper,
    IIdentifierVmService,
    IValuesService
} from '../../services';
import { RowViewModel, DateViewModel, FakeTagDateValue } from '../../widgets/models/table';
import { tagDefaultState, momentFormat } from '../../common/constants';
import { MomentHelper } from '../../common/helpers';
import { ValueTypes } from '../../common/ValueTypes';
import { ISeriesBuilder, SeriesMappingResult } from './';
import { mapConfiguration } from '../../common/helpers/valueViewModelHelper';
import { IdentifierQueryDto, IExportToCsvService, IIdentifierQueryService } from '../../services/data';
import { TableWidget } from '../../widgets/table/table.widget';
import { RowConfiguration } from '../../widgets/table/tableRows/tableRow.configuration';
import { TableWidgetConfiguration } from '../../widgets/table/table.configuration';
import { getUnitName } from '../../common';
import { ISeriesBuilderArguments } from './ISeriesBuilder';
import { formatValueForCsv } from '../../services/data/formatValueForCsv';

export interface ITableWidgetService extends ISeriesBuilder {
    prepareDates(interval: { beginMoment: moment.Moment, endMoment: moment.Moment }, aggregation: AggregationConfiguration): angular.IPromise<{ displayDate: string, actualDate: string, isoDate: string }[]>;
    getRowViewModels(rows: RowConfiguration[]): angular.IPromise<RowViewModel[]>;
    readData(configuration: TableWidgetConfiguration, interval: any, rows: IBindable[]): angular.IPromise<TableWidgetDto>;
    getDataTable(
        widget: TableWidget,
        aggregationColumnMode: string,
        dates: DateViewModel[],
        rowViewModels: RowViewModel[],
        hasTarget: boolean,
        hasAlarm: boolean
    ): any[][];
    mapSeriesToViewmodel(vm: IValueViewModel, r: RowConfiguration): RowViewModel;
    mapValueAccordingSerie(value: TagDateValue, row: RowViewModel): string | number;
}

export interface DateSet {
    displayDate: string;
    actualDate: string;
    isoDate: string;
}

export class TableWidgetService implements ITableWidgetService {
    static serviceId = 'tableWidgetService';

    private _alarmsScale: d3.scale.Ordinal<number, string>;
    private _qualityStatuses: string[];

    static $inject = [
        '$q',
        '$state',
        'identifierVmService',
        'dateAlignmentHelperService',
        'currentApplicationService',
        'aggregatedValuesService',
        'valuesService',
        'identifierQueryService',
        'exportToCsvService',
        'formatNumberHelper'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$state: angular.ui.IStateService,
        private _identifierVmService: IIdentifierVmService,
        private _dateAlignmentHelperService: IDateAlignmentHelperService,
        private _currentApplicationService: ICurrentApplicationService,
        private _aggregatedValuesService: IAggregatedValuesService,
        private _valuesService: IValuesService,
        private _identifierQueryService: IIdentifierQueryService,
        private _exportToCsvService: IExportToCsvService,
        private _formatNumberHelper: IFormatNumberHelper
    ) {

        this._qualityStatuses = ['OK', 'Warning', 'Error'];
        this._alarmsScale = d3.scale.ordinal<number, string>()
            .domain([1, 2, 3])
            .range(['Information', 'Warning', 'Critical']);
    }

    mapValueAccordingSerie(value: TagDateValue, row: RowViewModel): string | number {
        row.isUserComment = (value.valueType === ValueTypes.comment.key);
        return row.isUserComment
            ? value.comment
            : value.value;
    }

    getRowViewModels(rows: RowConfiguration[]): angular.IPromise<RowViewModel[]> {
        const defered = this._$q.defer<RowViewModel[]>();
        const promises: angular.IPromise<any>[] = [
            this._identifierVmService.getViewModelsFromApiFromGuidList(rows.map(d => d.guid)),
            this._identifierQueryService.getAll()
        ];

        this._$q.all(promises)
            .then(data => {
                const queries = data[1].map(dto => dto.query);
                const result: RowViewModel[] = rows.map(r => {
                    let vm = _.find(data[0], d => d.guid === r.guid);
                    if (!vm) {
                        vm = _.find(queries, q => q.guid === r.guid) as any;
                    }
                    const row = this.mapSeriesToViewmodel(vm, r);
                    return row;
                });
                defered.resolve(result);
            })
            .catch((reason) => defered.reject(reason));
        return defered.promise;
    }

    mapSeriesToViewmodel(vm: IValueViewModel, r: RowConfiguration): RowViewModel {
        let name = '';
        let unitName = '';
        let target = null;
        if (vm) {
            name = vm.name;
            unitName = getUnitName(vm.unitName, r.value);
            const targetParts = [];
            if (r.isTargetVisible) {
                targetParts.push(((vm.target || vm.target === 0) ? vm.target : '#NA') as any);
            }
            if (r.isRangeVisible) {
                targetParts.push((`[${vm.min} - ${vm.max}]`) as any);
            }
            target = targetParts.join(' ');
        }

        return {
            guid: vm ? vm.guid : r.guid,
            name: name,
            unit: unitName,
            values: [],
            target: target,
            style: {
                color: r.color
            },
            aggregated: undefined,
            type: vm ? vm.objectType : r.selectorType,
            dataType: vm ? vm.actualDataType : 0,
            redirectUrl: this.getRedirectUrl(vm),
            originalViewModel: vm as any,
            link: r.link,
            bound: r.isBoundToGroup,
            value: r.value,
            decimals: r.decimals,
            isAlarmVisible: r.isAlarmVisible,
            isUserComment: false
        };
    }

    private getRedirectUrl(vm) {
        if (vm) {
            return vm.objectType === 0
                ? this._$state.href(tagDefaultState, { tagDefinitionGuid: vm.guid })
                : this._$state.href('linked', { linkedGuid: vm.guid });
        }
        return '';
    }

    prepareDates(interval: { beginMoment: moment.Moment, endMoment: moment.Moment },
        aggregation: AggregationConfiguration): angular.IPromise<DateSet[]> {
        const defered = this._$q.defer<DateSet[]>();
        try {
            const format = MomentHelper.getFormat(aggregation.period.unit);
            const dates = [];
            const period = new TimeInterval();
            const config = this._currentApplicationService.getApplicationConfiguration();
            if (aggregation.period.interval && aggregation.period.interval > 0) {
                period.interval = aggregation.period.interval;
                period.unit = aggregation.period.unit;

            } else {
                period.interval = config.interval;
                period.unit = config.intervalTimeUnit;
            }
            const duration = period.duration();
            this._dateAlignmentHelperService.getNext(interval.beginMoment)
                .then(alignedMoment => {
                    const current = alignedMoment.clone();
                    while (current < interval.endMoment) {
                        dates.push({
                            displayDate: current.format(format),
                            actualDate: current.format(momentFormat),
                            isoDate: current.toISOString()
                        });
                        current.add(duration);
                    }
                    defered.resolve(dates);
                })
                .catch(reason => defered.reject(reason));
        } catch (e) {
            defered.reject(e);
        }
        return defered.promise;
    }

    readData(
        configuration: TableWidgetConfiguration,
        interval: any,
        rows: IBindable[]
    ): angular.IPromise<TableWidgetDto> {
        const beginDate = interval.beginMoment.toISOString();
        const endDate = interval.endMoment.toISOString();
        const map = rows.filter(r => !r.isQuery).map(r => {
            return {
                guid: r.guid,
                value: (r && r.value) ? r.value : 'average'
            };
        });
        const config: GetAggregatedValuesDto = {
            beginDateTime: beginDate,
            endDateTime: endDate,
            aggregationInterval: configuration.aggregation.period.interval,
            aggregationMode: configuration.aggregation.mode,
            aggregationUnit: configuration.aggregation.period.unit,
            hasQuality: configuration.isQualityVisible,
            hasAlarm: _.some(configuration.rows, r => r.isAlarmVisible && !r.isQuery),
            list: map
        };
        const promises: angular.IPromise<any>[] = [
            this._aggregatedValuesService.getAggregatedValuesList(config)
        ];
        if (configuration.hasTrend && configuration.aggregation.mode !== AggregationMode.None) {
            promises.push(this._valuesService.getValues(map, beginDate, endDate));
        }
        return this._$q
            .all(promises)
            .then(responses => {
                const result = responses[0];
                if (responses.length > 1) {
                    result.trendValues = responses[1];
                } else if (configuration.hasTrend) {
                    result.trendValues = result.values;
                }
                return result;

            });
    }

    getValueType(key): string {
        return ValueTypes.getValueType(key);
    }

    getDataTable(
        widget: TableWidget,
        aggregationColumnMode: string,
        dates: DateViewModel[],
        rowViewModels: RowViewModel[],
        hasTarget: boolean,
        hasAlarm: boolean
    ) {
        let dataArray = [];
        if (widget.configuration.isVertical) {
            dataArray = this.buildVerticalTable(widget, aggregationColumnMode, dates,
                rowViewModels, hasTarget, hasAlarm);
        } else {
            dataArray = this.buildHorizontalTable(widget, aggregationColumnMode, dates,
                rowViewModels, hasTarget, hasAlarm);
        }
        return dataArray;
    }

    private buildVerticalTable(
        widget: TableWidget,
        aggregationColumnMode: string,
        dates: DateViewModel[],
        rowViewModels: RowViewModel[],
        hasTarget: boolean,
        hasAlarm: boolean
    ): any[] {
        const rows = [];
        const formatter = this._formatNumberHelper.getFormatter(null, false);
        if (widget.configuration.nameConfiguration.forceName) {
            rows.push(['']);
        } else {
            if (widget.configuration.nameConfiguration.hasGroupName) {
                rows.push(['']);
            }
            if (widget.configuration.nameConfiguration.hasName) {
                rows.push(['']);
            }
            if (widget.configuration.nameConfiguration.hasShortComment) {
                rows.push(['Comment']);
            }
            if (widget.configuration.nameConfiguration.hasUnit) {
                rows.push(['Unit']);
            }
            if (widget.configuration.nameConfiguration.hasValueType) {
                rows.push(['Value']);
            }
        }
        dates.forEach(d => {
            rows.push([moment(d.isoDate).format(momentFormat)]);
        });
        if (widget.configuration.aggregationColumnMode) {
            rows.push([aggregationColumnMode]);
        }
        if (hasTarget) {
            rows.push(['Target']);
        }
        if (widget.configuration.isQualityVisible) {
            rows.push(['Quality']);
        }
        if (widget.configuration.showOwnFields) {
            this._exportToCsvService.getUserFieldsHeaders(rowViewModels.map(rvm => {
                return {
                    customProperties: rvm.customProperties || {}
                };
            }))
                .forEach(h => rows.push([h]));
        }
        if (hasAlarm) {
            rows.push(['Alarm']);
        }
        rowViewModels.forEach((d, i) => {
            let rowIndex = 0;
            if (widget.configuration.nameConfiguration.forceName) {
                rows[rowIndex].push(d.name);
                rowIndex++;
            } else {
                if (widget.configuration.nameConfiguration.hasGroupName) {
                    rows[rowIndex].push(d.originalViewModel.groupList[0].uniqueName);
                    rowIndex++;
                }
                if (widget.configuration.nameConfiguration.hasName) {
                    rows[rowIndex].push(d.name);
                    rowIndex++;
                }
                if (widget.configuration.nameConfiguration.hasShortComment) {
                    rows[rowIndex].push(d.originalViewModel.shortComment);
                    rowIndex++;
                }
                if (widget.configuration.nameConfiguration.hasUnit) {
                    rows[rowIndex].push(d.unit);
                    rowIndex++;
                }
                if (widget.configuration.nameConfiguration.hasValueType) {
                    rows[rowIndex].push(this.getValueType((d as any).value));
                    rowIndex++;
                }
            }

            this.getValues(d, formatter)
                .forEach(v => {
                    rows[rowIndex].push(v);
                    rowIndex++;
                });

            if (widget.configuration.aggregationColumnMode) {
                let aggregatedValue = d.aggregated ? d.aggregated : d.actualAggregated;
                aggregatedValue = aggregatedValue ? formatValueForCsv(Number(aggregatedValue), formatter) : aggregatedValue;
                rows[rowIndex].push(aggregatedValue);
                rowIndex++;
            }
            if (hasTarget) {
                rows[rowIndex].push(d.target);
                rowIndex++;
            }
            if (widget.configuration.isQualityVisible) {
                rows[rowIndex].push(this._qualityStatuses[d.status]);
                rowIndex++;
            }
            if (widget.configuration.showOwnFields) {
                this._exportToCsvService.getUserFieldsValues(d.customProperties || {})
                    .forEach(h => {
                        rows[rowIndex].push(h);
                        rowIndex++;
                    });
            }
            if (hasAlarm) {
                rows[rowIndex].push(d.alarm ? this._alarmsScale(d.alarm) : '');
                rowIndex++;
            }
        });
        return rows;
    }

    private buildHorizontalTable(widget: TableWidget,
        aggregationColumnMode: string,
        dates: DateViewModel[],
        rowViewModels: RowViewModel[],
        hasTarget: boolean,
        hasAlarm: boolean
    ): any[] {
        const headers = this.getHorizontalHeaders(widget, rowViewModels, aggregationColumnMode, dates, hasTarget, hasAlarm);
        const rows = this.getHorizontalRows(widget, rowViewModels, hasTarget, hasAlarm);
        return [headers].concat(rows);
    }

    private getHorizontalHeaders(
        widget: TableWidget,
        rowViewModels: RowViewModel[],
        aggregationColumnMode: string,
        dates: DateViewModel[],
        hasTarget: boolean,
        hasAlarm: boolean
    ) {
        const headers: string[] = [];
        if (widget.configuration.nameConfiguration.forceName) {
            headers.push('Name');
        } else {
            if (widget.configuration.nameConfiguration.hasGroupName) {
                headers.push('Group name');
            }
            if (widget.configuration.nameConfiguration.hasName) {
                headers.push('Name');
            }
            if (widget.configuration.nameConfiguration.hasShortComment) {
                headers.push('Comment');
            }
            if (widget.configuration.nameConfiguration.hasUnit) {
                headers.push('Unit');
            }
            if (widget.configuration.nameConfiguration.hasValueType) {
                headers.push('Value');
            }
        }
        dates.forEach(d => {
            headers.push(moment(d.isoDate).format(momentFormat));
        });
        if (widget.configuration.aggregationColumnMode) {
            headers.push(aggregationColumnMode);
        }
        if (hasTarget) {
            headers.push('Target');
        }
        if (widget.configuration.isQualityVisible) {
            headers.push('Quality');
        }
        if (widget.configuration.showOwnFields) {
            this._exportToCsvService.getUserFieldsHeaders(rowViewModels.map(rvm => {
                return {
                    customProperties: rvm.customProperties || {}
                };
            }))
                .forEach(h => headers.push(h));
        }
        if (hasAlarm) {
            headers.push('Alarm');
        }
        return headers;
    }

    private getHorizontalRows(widget: TableWidget,
        vms: RowViewModel[],
        hasTarget: boolean,
        hasAlarm: boolean
    ): string[][] {
        const formatter = this._formatNumberHelper.getFormatter(null, false);
        const rows = vms.map(vm => {
            const row = [];
            if (widget.configuration.nameConfiguration.forceName) {
                row.push(vm.name);
            } else {
                if (widget.configuration.nameConfiguration.hasGroupName) {
                    row.push(vm.originalViewModel.groupList[0].uniqueName);
                }
                if (widget.configuration.nameConfiguration.hasName) {
                    row.push(vm.name);
                }
                if (widget.configuration.nameConfiguration.hasShortComment) {
                    row.push(vm.originalViewModel.shortComment);
                }
                if (widget.configuration.nameConfiguration.hasUnit) {
                    row.push(vm.unit);
                }
                if (widget.configuration.nameConfiguration.hasValueType) {
                    row.push(this.getValueType(vm.unit));
                }
            }
            row.push(...this.getValues(vm, formatter));
            if (widget.configuration.aggregationColumnMode) {
                let aggregatedValue = vm.aggregated ? vm.aggregated : vm.actualAggregated;
                aggregatedValue = aggregatedValue ? formatValueForCsv(Number(aggregatedValue), formatter) : aggregatedValue;
                row.push(aggregatedValue);
            }
            if (hasTarget) {
                row.push(vm.target);
            }
            if (widget.configuration.isQualityVisible) {
                row.push(this._qualityStatuses[vm.status]);
            }
            if (widget.configuration.showOwnFields) {
                this._exportToCsvService.getUserFieldsValues(vm.customProperties || {})
                    .forEach(h => row.push(h));
            }
            if (hasAlarm) {
                row.push(vm.alarm ? this._alarmsScale(vm.alarm) : '');
            }
            return row;
        });
        return rows;
    }

    private getValues(vm: RowViewModel, formatter: Intl.NumberFormat): string[] {
        let values: string[] = [];

        const formatFunction = vm.dataType === ActualDataType.Float
            ? (d: FakeTagDateValue) => formatValueForCsv(d.value, formatter)
            : (d: FakeTagDateValue) => d.formattedValue;
        // numeric values
        values = vm.values.map(d => formatFunction(d));
        return values;
    }

    buildSeriesFromViewModels(args: ISeriesBuilderArguments): angular.IPromise<SeriesMappingResult> {
        const { configuration, viewModels, map, errors } = args;
        const mapped = mapConfiguration(configuration.rows as any[], viewModels, map, errors);
        const error = mapped.error;
        const series = mapped.series
            .filter(s => !s.missing)
            .map(r => r.configuration.isQuery
                ? r
                : this.mapSeriesToViewmodel(r.viewModel, r.configuration as any)
            );
        return this._$q.when({
            error,
            series: series,
            map
        });
    }
}